import {
  MedicationStatement,
  CodeableConcept,
  Patient,
  Encounter,
  Dosage,
} from '@medplum/fhirtypes';
import { createReference } from '@medplum/core';
import type { MedicationTranslationKey } from '@consts/medication.consts';

// =============================================================================
// GETTERS - Safe access to nested fields
// =============================================================================

export function getMedicationName(med: MedicationStatement): string | undefined {
  const concept = med.medicationCodeableConcept;
  return concept?.text || concept?.coding?.[0]?.display;
}

export function getDosage(med: MedicationStatement): Dosage | undefined {
  return med.dosage?.[0];
}

export function getDoseValue(med: MedicationStatement): number | undefined {
  return med.dosage?.[0]?.doseAndRate?.[0]?.doseQuantity?.value;
}

export function getDoseUnit(med: MedicationStatement): string | undefined {
  return med.dosage?.[0]?.doseAndRate?.[0]?.doseQuantity?.unit;
}

export function getFrequency(med: MedicationStatement): number | undefined {
  return med.dosage?.[0]?.timing?.repeat?.frequency;
}

export function getPeriod(med: MedicationStatement): number | undefined {
  return med.dosage?.[0]?.timing?.repeat?.period;
}

export function getPeriodUnit(
  med: MedicationStatement
): 's' | 'min' | 'h' | 'd' | 'wk' | 'mo' | 'a' | undefined {
  return med.dosage?.[0]?.timing?.repeat?.periodUnit;
}

export function getRoute(med: MedicationStatement): CodeableConcept | undefined {
  return med.dosage?.[0]?.route;
}

export function getDosageText(med: MedicationStatement): string | undefined {
  return med.dosage?.[0]?.text;
}

export function getAsNeeded(med: MedicationStatement): boolean | undefined {
  return med.dosage?.[0]?.asNeededBoolean;
}

export function getNoteText(med: MedicationStatement): string | undefined {
  return med.note?.[0]?.text;
}

export function getStatusReasonText(med: MedicationStatement): string | undefined {
  return med.statusReason?.[0]?.text;
}

export function getStatusReason(med: MedicationStatement): CodeableConcept | undefined {
  return med.statusReason?.[0];
}

export function getContextEncounterId(med: MedicationStatement): string | undefined {
  return med.context?.reference?.replace('Encounter/', '');
}

export function getInformationSourceId(med: MedicationStatement): string | undefined {
  return med.informationSource?.reference?.replace(
    /^(Practitioner|Patient|RelatedPerson|Organization)\//,
    ''
  );
}

export function parseEffectiveStart(med: MedicationStatement): Date | null {
  const start = med.effectivePeriod?.start;
  return start ? new Date(start) : null;
}

export function parseEffectiveEnd(med: MedicationStatement): Date | null {
  const end = med.effectivePeriod?.end;
  return end ? new Date(end) : null;
}

// =============================================================================
// SETTERS - Immutable updates returning new MedicationStatement
// =============================================================================

export function toFhirDateString(date: Date | null): string | undefined {
  return date ? date.toISOString().split('T')[0] : undefined;
}

export function setMedicationCodeableConcept(
  med: MedicationStatement,
  concept: CodeableConcept | undefined
): MedicationStatement {
  return { ...med, medicationCodeableConcept: concept };
}

export function setStatus(
  med: MedicationStatement,
  status: MedicationStatement['status']
): MedicationStatement {
  return { ...med, status };
}

export function setDoseQuantity(
  med: MedicationStatement,
  value: number | undefined,
  unit: string | undefined
): MedicationStatement {
  const existingDosage = med.dosage?.[0] ?? {};
  const existingDoseAndRate = existingDosage.doseAndRate?.[0] ?? {};

  if (value === undefined && unit === undefined) {
    const { doseAndRate: _doseAndRate, ...restDosage } = existingDosage;
    return {
      ...med,
      dosage: Object.keys(restDosage).length > 0 ? [restDosage] : undefined,
    };
  }

  return {
    ...med,
    dosage: [
      {
        ...existingDosage,
        doseAndRate: [
          {
            ...existingDoseAndRate,
            doseQuantity: {
              value,
              unit,
              system: 'http://unitsofmeasure.org',
              code: unit,
            },
          },
        ],
      },
    ],
  };
}

export function setTiming(
  med: MedicationStatement,
  frequency: number | undefined,
  period: number | undefined,
  periodUnit: 'd' | 'wk' | 'mo' | undefined
): MedicationStatement {
  const existingDosage = med.dosage?.[0] ?? {};

  if (!frequency && !period && !periodUnit) {
    const { timing: _timing, ...restDosage } = existingDosage;
    return {
      ...med,
      dosage: Object.keys(restDosage).length > 0 ? [restDosage] : undefined,
    };
  }

  return {
    ...med,
    dosage: [
      {
        ...existingDosage,
        timing: {
          repeat: {
            frequency,
            period,
            periodUnit,
          },
        },
      },
    ],
  };
}

export function setRoute(
  med: MedicationStatement,
  route: CodeableConcept | undefined
): MedicationStatement {
  const existingDosage = med.dosage?.[0] ?? {};

  if (!route) {
    const { route: _route, ...restDosage } = existingDosage;
    return {
      ...med,
      dosage: Object.keys(restDosage).length > 0 ? [restDosage] : undefined,
    };
  }

  return {
    ...med,
    dosage: [{ ...existingDosage, route }],
  };
}

export function setDosageText(
  med: MedicationStatement,
  text: string | undefined
): MedicationStatement {
  const existingDosage = med.dosage?.[0] ?? {};

  if (!text) {
    const { text: _text, ...restDosage } = existingDosage;
    return {
      ...med,
      dosage: Object.keys(restDosage).length > 0 ? [restDosage] : undefined,
    };
  }

  return {
    ...med,
    dosage: [{ ...existingDosage, text }],
  };
}

export function setAsNeeded(
  med: MedicationStatement,
  asNeeded: boolean | undefined
): MedicationStatement {
  const existingDosage = med.dosage?.[0] ?? {};

  if (asNeeded === undefined) {
    const { asNeededBoolean: _asNeededBoolean, ...restDosage } = existingDosage;
    return {
      ...med,
      dosage: Object.keys(restDosage).length > 0 ? [restDosage] : undefined,
    };
  }

  return {
    ...med,
    dosage: [{ ...existingDosage, asNeededBoolean: asNeeded }],
  };
}

export function setNote(
  med: MedicationStatement,
  text: string | undefined
): MedicationStatement {
  return {
    ...med,
    note: text ? [{ text }] : undefined,
  };
}

export function setStatusReason(
  med: MedicationStatement,
  reason: CodeableConcept | string | undefined
): MedicationStatement {
  if (!reason) {
    const { statusReason: _statusReason, ...rest } = med;
    return rest;
  }

  const concept: CodeableConcept =
    typeof reason === 'string' ? { text: reason } : reason;

  return {
    ...med,
    statusReason: [concept],
  };
}

export function setEffectivePeriod(
  med: MedicationStatement,
  start: Date | null,
  end: Date | null
): MedicationStatement {
  if (!start && !end) {
    const { effectivePeriod: _effectivePeriod, ...rest } = med;
    return rest;
  }

  return {
    ...med,
    effectivePeriod: {
      start: toFhirDateString(start),
      end: toFhirDateString(end),
    },
  };
}

export function setDateAsserted(
  med: MedicationStatement,
  date: Date | string | undefined
): MedicationStatement {
  if (!date) {
    const { dateAsserted: _dateAsserted, ...rest } = med;
    return rest;
  }

  const dateStr = typeof date === 'string' ? date : toFhirDateString(date);
  return { ...med, dateAsserted: dateStr };
}

// =============================================================================
// FACTORY - Create new MedicationStatement
// =============================================================================

export function createNewMedicationStatement(
  patient: Patient,
  encounter: Encounter
): MedicationStatement {
  return {
    resourceType: 'MedicationStatement',
    status: 'active',
    subject: createReference(patient),
    context: createReference(encounter),
    dateAsserted: new Date().toISOString().split('T')[0],
  };
}

// =============================================================================
// HELPERS - Convenience functions
// =============================================================================

export function hasMedicationName(med: MedicationStatement): boolean {
  const concept = med.medicationCodeableConcept;
  return Boolean(concept?.coding?.[0]?.code || concept?.text);
}

export function isStopped(med: MedicationStatement): boolean {
  return med.status === 'stopped' || med.status === 'completed';
}

export function isStoppedInEncounter(
  med: MedicationStatement,
  encounterId: string | undefined
): boolean {
  if (!encounterId) {
    return false;
  }
  return isStopped(med) && getContextEncounterId(med) === encounterId;
}

export function formatFrequency(
  med: MedicationStatement,
  t: (key: MedicationTranslationKey) => string
): string {
  const frequency = getFrequency(med);
  const period = getPeriod(med);
  const periodUnit = getPeriodUnit(med);

  if (!frequency && !period) {
    return '';
  }

  const freq = frequency || 1;
  const per = period || 1;
  const unit = periodUnit || 'd';

  if (per === 1 && unit === 'd') {
    if (freq === 1) {
      return t('medications.frequency.onceDaily');
    }
    if (freq === 2) {
      return t('medications.frequency.twiceDaily');
    }
    if (freq === 3) {
      return t('medications.frequency.thriceDaily');
    }
    return `${freq}x ${t('medications.frequency.daily')}`;
  }

  if (per === 1 && unit === 'wk') {
    if (freq === 1) {
      return t('medications.frequency.onceWeekly');
    }
    return `${freq}x ${t('medications.frequency.weekly')}`;
  }

  if (unit === 'wk' && per === 2) {
    return t('medications.frequency.everyTwoWeeks');
  }

  if (per === 1 && unit === 'mo') {
    if (freq === 1) {
      return t('medications.frequency.onceMonthly');
    }
    return `${freq}x ${t('medications.frequency.monthly')}`;
  }

  const unitLabel =
    unit === 'd'
      ? t('medications.frequency.days')
      : unit === 'wk'
        ? t('medications.frequency.weeks')
        : t('medications.frequency.months');

  return `${freq}x / ${per} ${unitLabel}`;
}

export function formatDosageSummary(
  med: MedicationStatement,
  t: (key: MedicationTranslationKey) => string
): string {
  const parts: string[] = [];

  const doseValue = getDoseValue(med);
  const doseUnit = getDoseUnit(med);
  if (doseValue) {
    parts.push(`${doseValue}${doseUnit || ''}`);
  }

  const route = getRoute(med);
  if (route?.text || route?.coding?.[0]?.display) {
    parts.push(route.text || route.coding?.[0]?.display || '');
  }

  const freqStr = formatFrequency(med, t);
  if (freqStr) {
    parts.push(freqStr);
  }

  if (getAsNeeded(med)) {
    parts.push(t('medications.asNeeded'));
  }

  return parts.join(' ');
}

export function formatDateRange(
  med: MedicationStatement,
  t: (key: MedicationTranslationKey) => string,
  locale?: string
): string {
  const formatDate = (date: Date | null) => {
    if (!date) {
      return '';
    }
    const effectiveLocale = locale || 'en-GB';
    return date.toLocaleDateString(effectiveLocale, { month: 'short', year: 'numeric' });
  };

  const start = parseEffectiveStart(med);
  const end = parseEffectiveEnd(med);

  if (start && end) {
    return `${formatDate(start)} – ${formatDate(end)}`;
  }

  if (start) {
    return `${t('medications.dateRange.since')} ${formatDate(start)}`;
  }

  return '';
}
